/**
 * Elasticsearch inserts vs updates, per index, per day.
 *
 * Uses the incumbent dashboard's split exactly — a document counts as inserted
 * only when its insert and update timestamps fall on the same day, and as
 * updated when they differ — so a re-touched document is never double-counted
 * as new, and both dashboards report the same thing.
 *
 * This is the panel that surfaces a dead sync. On 2026-07-30 the video index
 * showed 0 inserted and 0 updated across all seven days while holding 480 M
 * documents, because the video leg of Logstash had stopped 11 days earlier.
 */
import { useEsActivity, useEsHealth } from '../api/queries'
import { GroupedBars } from '../components/charts'
import { PanelState, Pill, Stat, Tile } from '../components/ui'
import { age, n, headline } from '../components/format'

export function EsActivity() {
  const act = useEsActivity()
  const health = useEsHealth()

  if (act.data && 'configured' in act.data && act.data.configured === false) {
    return <Tile title="Elasticsearch activity"><p className="panel-load">not configured</p></Tile>
  }
  const indices = act.data && 'indices' in act.data ? act.data.indices : {}
  const healthMap = health.data && 'indices' in health.data ? health.data.indices : {}

  return (
    <>
      {Object.entries(indices).map(([name, v]) => {
        const h = healthMap[name]
        const stale = h && 'stale' in h ? h.stale : false
        const ageS = h && 'age_seconds' in h ? h.age_seconds : null
        return (
          <Tile key={name}
                title={`ES · ${name}`}
                hint={h && 'index' in h ? `${h.index} · ${n('docs' in h ? h.docs : null)} docs` : undefined}>
            <PanelState isLoading={act.isLoading} error={act.error}>
              {'error' in v ? <p className="panel-err">{v.error}</p> : (
                <>
                  <div style={{ marginBottom: 12 }}>
                    {stale
                      ? <Pill state="crit">stale · {age(ageS)}</Pill>
                      : <Pill state="ok">current · {age(ageS)}</Pill>}
                  </div>
                  <div className="stats-row" style={{ marginBottom: 14 }}>
                    <Stat label="inserted · 7d" value={headline(v.inserted_total)}
                          state={v.inserted_total === 0 ? 'crit' : undefined} />
                    <Stat label="updated · 7d" value={headline(v.updated_total)}
                          state={v.updated_total === 0 ? 'crit' : undefined} />
                    <Stat label="inserted · 1d" value={headline(v.inserted_per_day.at(-1) ?? 0)} />
                    <Stat label="updated · 1d" value={headline(v.updated_per_day.at(-1) ?? 0)} />
                  </div>
                  <GroupedBars days={v.days} a={v.inserted_per_day} b={v.updated_per_day}
                               aLabel="inserted" bLabel="updated"
                               label={`${name}: daily inserts and updates over 7 days`} />
                  {v.inserted_total === 0 && v.updated_total === 0 ? (
                    <p className="panel-err" style={{ marginTop: 10 }}>
                      no writes in 7 days — the sync for this index has stopped
                    </p>
                  ) : null}
                </>
              )}
            </PanelState>
          </Tile>
        )
      })}
    </>
  )
}
